<template>
  <div
    v-if="total === 0"
    :style="{ display: 'flex', flexDirection: 'column', gap: '7px', minHeight: '38px', justifyContent: 'center' }"
  >
    <span :style="{ display: 'inline-flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: 'var(--p-text-muted-color)' }">
      <i class="pi pi-list" :style="{ fontSize: '13px', color: 'var(--p-surface-400)' }" />
      <span>Showing <strong :style="{ color: 'var(--p-text-color)' }">all {{ kind }}</strong>.</span>
    </span>
    <span
      :style="{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontStyle: 'italic', color: 'var(--p-surface-400)', paddingLeft: '21px' }"
    >
      <i class="pi pi-info-circle" :style="{ fontSize: '11px', fontStyle: 'normal', opacity: 0.8 }" />
      <span>Use <strong :style="{ color: 'var(--p-text-muted-color)', fontWeight: 600, fontStyle: 'normal' }">Filters</strong> on the left to narrow the list.</span>
    </span>
  </div>

  <div v-else :style="{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', minHeight: '38px' }">
    <span :style="{ fontSize: '13.5px', color: 'var(--p-text-muted-color)', flexShrink: 0 }">Showing {{ kind }} where</span>
    <template v-for="(g, gi) in groups" :key="g.key">
      <span v-if="gi > 0" :style="andStyle">and</span>
      <span v-for="p in g.pills" :key="p.key" :style="pillStyle">
        <span :style="{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }">{{ p.label }}</span>
        <button
          v-if="p.remove"
          title="Remove"
          :style="{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '18px', height: '18px', borderRadius: '999px', border: 0, background: 'transparent', cursor: 'pointer', color: 'var(--p-surface-400)', flexShrink: 0 }"
          @click="p.remove()"
        >
          <i class="pi pi-times" :style="{ fontSize: '10px' }" />
        </button>
      </span>
    </template>
    <template v-for="(a, i) in advanced" :key="a.key">
      <span v-if="groups.length > 0 || i > 0" :style="andStyle">and</span>
      <span :style="pillStyle">
        <span :style="{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }">{{ a.label }}</span>
      </span>
    </template>
    <button
      title="Clear all filters"
      :style="{ display: 'inline-flex', alignItems: 'center', gap: '5px', height: '26px', padding: '0 9px', borderRadius: '999px', border: '1px solid var(--p-content-border-color)', background: 'transparent', color: 'var(--p-text-muted-color)', cursor: 'pointer', fontFamily: 'inherit', fontSize: '12px', fontWeight: 600, marginLeft: '2px' }"
      @click="emit('clear')"
    >
      <i class="pi pi-times" :style="{ fontSize: '10px' }" />Clear
    </button>
  </div>
</template>

<script setup>
// Plain-language search summary — renders the structured query as a sentence
// with removable pills, for the searchUi="summary" mode. Reads the same
// fieldView the sidebar uses; writes through the same search API. Ported from
// list-edit-shell.jsx SearchSummary.
import { computed } from 'vue'

const props = defineProps({
  search: { type: Object, required: true },
  fieldMeta: { type: Object, default: () => ({}) },
  kindLabel: { type: String, default: null },
})
const emit = defineEmits(['clear'])

const fv = computed(() => props.search.fieldView || { byField: {}, advanced: [] })
const fieldLabel = (k) => (props.fieldMeta[k] && props.fieldMeta[k].label) || k
const valLabel = (k, v) => {
  const em = props.fieldMeta[k] && props.fieldMeta[k].enumMeta && props.fieldMeta[k].enumMeta[v]
  return (em && em.label) || v
}
const rangeText = (r) => {
  if (r.lower && r.upper) return `${r.lower}–${r.upper}`
  if (r.lower) return `${r.lower} or more`
  if (r.upper) return `up to ${r.upper}`
  return 'any'
}
const activeKeys = computed(() =>
  Object.keys(fv.value.byField).filter((k) => (fv.value.byField[k].values && fv.value.byField[k].values.size) || fv.value.byField[k].range),
)
const total = computed(
  () =>
    activeKeys.value.reduce((n, k) => n + ((fv.value.byField[k].values && fv.value.byField[k].values.size) || 0) + (fv.value.byField[k].range ? 1 : 0), 0) +
    (fv.value.advanced ? fv.value.advanced.length : 0),
)

// Build pill descriptors per active field group.
const groups = computed(() =>
  activeKeys.value.map((k) => {
    const slot = fv.value.byField[k]
    const pills = []
    if (slot.values && slot.values.size) {
      const vals = [...slot.values]
      pills.push({ key: k + ':v', label: `${fieldLabel(k)} is ${vals.map((v) => valLabel(k, v)).join(' or ')}`, remove: () => props.search.clearField(k) })
    }
    if (slot.range) pills.push({ key: k + ':r', label: `${fieldLabel(k)} ${rangeText(slot.range)}`, remove: () => props.search.clearField(k) })
    return { key: k, pills }
  }),
)
const advanced = computed(() => (fv.value.advanced || []).map((a, i) => ({ key: 'adv' + i, label: a.text || 'advanced condition' })))

const pillStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  height: '26px',
  padding: '0 4px 0 10px',
  borderRadius: '999px',
  background: 'var(--p-highlight-background, color-mix(in srgb, var(--p-primary-500) 13%, transparent))',
  border: '1px solid color-mix(in srgb, var(--p-primary-500) 35%, transparent)',
  fontSize: '12.5px',
  color: 'var(--p-text-color)',
  maxWidth: '260px',
}
const andStyle = { fontSize: '12px', fontWeight: 700, color: 'var(--p-surface-400)' }
const kind = computed(() => (props.kindLabel || 'records').toLowerCase())
</script>
